import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const SPEECH_RMS = 0.02;
const BARGE_RMS = 0.05;
const END_SILENCE_MS = 1300;
const NO_SPEECH_EXIT_MS = 30000;

// Hands-free loop: listen (auto-stop on silence) → transcribe → onTranscript → caller calls speaking() → barge-in listen → resume()
export const useHandsFree = ({ onTranscript, onBargeIn, language }) => {
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | listening | transcribing | thinking | speaking
  const [level, setLevel] = useState(0);
  const activeRef = useRef(false);
  const bargeRef = useRef(false);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const ctxRef = useRef(null);
  const timerRef = useRef(null);
  const exitBargeRef = useRef(null);

  const cleanupAudio = () => {
    clearInterval(timerRef.current);
    exitBargeRef.current = null;
    try { recRef.current?.state !== 'inactive' && recRef.current?.stop(); } catch (e) { console.warn('[handsfree] recorder already stopped', e); }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    ctxRef.current?.close().catch(() => {});
    recRef.current = null; streamRef.current = null; ctxRef.current = null;
  };

  const exit = (msg) => {
    if (msg) toast.info(msg);
    activeRef.current = false; bargeRef.current = false;
    setActive(false); setPhase('idle'); setLevel(0);
    cleanupAudio();
  };

  const listen = useCallback(async () => {
    if (!activeRef.current) return;
    cleanupAudio();
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch (e) {
      console.warn('[handsfree] microphone access failed', e);
      toast.error('Accès au micro refusé');
      exit();
      return;
    }
    if (!activeRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
    streamRef.current = stream;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';

    let chunks = [], hadSpeech = false, lastSpeech = Date.now(), started = Date.now(), loud = 0;

    const startRecorder = () => {
      chunks = [];
      const rec = new MediaRecorder(stream, { mimeType: mime });
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        if (rec.discard) return;
        clearInterval(timerRef.current);
        stream.getTracks().forEach((t) => t.stop());
        ctx.close().catch(() => {});
        if (!activeRef.current) return;
        const blob = new Blob(chunks, { type: 'audio/webm' });
        if (!hadSpeech || blob.size < 2000) { listen(); return; }
        setPhase('transcribing');
        try {
          const { text } = await api.transcribe(blob, language);
          if (!activeRef.current) return;
          if (text && text.trim().length > 1) { setPhase('thinking'); onTranscript(text.trim()); } else listen();
        } catch (e) { toast.error(e.message); listen(); }
      };
      rec.start(250);
      recRef.current = rec;
      return rec;
    };

    let rec = startRecorder();
    // Barge-in detected or TTS finished: switch from "speaking" monitoring to a fresh normal listening session
    const toListening = (speechDetected) => {
      bargeRef.current = false;
      rec.discard = true; rec.stop();
      rec = startRecorder();
      started = Date.now(); lastSpeech = started; hadSpeech = speechDetected; loud = 0;
      setPhase('listening');
    };
    exitBargeRef.current = () => toListening(false);
    setPhase(bargeRef.current ? 'speaking' : 'listening');

    timerRef.current = setInterval(() => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      setLevel(rms);
      const now = Date.now();
      if (bargeRef.current) {
        loud = rms > BARGE_RMS ? loud + 1 : 0;
        if (loud >= 2) { onBargeIn?.(); toListening(true); }
        return;
      }
      if (rms > SPEECH_RMS) { hadSpeech = true; lastSpeech = now; }
      if (!hadSpeech && now - started > NO_SPEECH_EXIT_MS) {
        exit(language === 'fr' ? 'Mode mains libres arrêté (aucune parole détectée)' : 'Hands-free stopped (no speech detected)');
      } else if ((hadSpeech && now - lastSpeech > END_SILENCE_MS && now - started > 800) || now - started > 30000) {
        rec.stop();
      }
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, onTranscript, onBargeIn]);

  const start = useCallback(() => { activeRef.current = true; bargeRef.current = false; setActive(true); listen(); }, [listen]);
  const stop = useCallback(() => exit(), []); // eslint-disable-line react-hooks/exhaustive-deps
  // Aurra is about to speak: keep the mic open so the user can interrupt
  const speaking = useCallback(() => { if (!activeRef.current) return; bargeRef.current = true; setPhase('speaking'); listen(); }, [listen]);
  // TTS ended (or was interrupted by button): go back to normal listening
  const resume = useCallback(() => {
    if (!activeRef.current) return;
    if (bargeRef.current) { if (exitBargeRef.current) exitBargeRef.current(); else bargeRef.current = false; } else if (!recRef.current) listen();
  }, [listen]);

  useEffect(() => () => { activeRef.current = false; cleanupAudio(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { active, phase, level, start, stop, speaking, resume };
};
